import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { audiencesService } from "../../services/audiences";
import { getPlatform } from "../../utils/platforms";
import { formatNumber, formatOutlier } from "../../utils/numbers";
import VideoDetailModal from "../../components/modals/VideoDetailModal";
import FitScoreRadial from "../../components/audience/FitScoreRadial";
import SortableTh from "../../components/common/SortableTh";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import { getTotalPages, parsePaginatedResponse } from "../../utils/pagination";
import { smoothScrollAudiencePageTop } from "../../utils/scroll";
import MiniDistribution from "../../components/patterns/MiniDistribution";

const PAGE_SIZE = 20;

const TOOLTIP = {
  fit: "How well this video matches the audience category, when the audience has a category and a fit score exists.",
};

const VsCreatorCell = ({ outlierRatio }) => {
  const outlier = formatOutlier(outlierRatio);
  if (!outlier) return <span className="text-sm text-theme-text opacity-40">—</span>;
  return (
    <span
      className={`text-sm font-medium ${
        outlierRatio === 1 ? "text-theme-text opacity-60" : outlier.above ? "text-green-500" : "text-red-400"
      }`}
    >
      {outlier.text}
    </span>
  );
};

const formatUploadedAt = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const formatEngagementPct = (engagement) => {
  if (engagement == null || !Number.isFinite(Number(engagement))) return null;
  const n = Number(engagement);
  if (n === 0) return null;
  return `${n.toFixed(2)}%`;
};

/** Count or rate column: right-aligned value plus page-level MiniDistribution when data exists. */
const VideoMetricWithChart = ({ value, distribution, display, showChart }) => {
  const hasDisplay = display != null && display !== "";
  return (
    <td className="px-4 py-3 whitespace-nowrap text-right">
      <div className="flex items-center justify-end gap-2 min-w-0">
        {hasDisplay ? (
          <span className="text-sm text-theme-text shrink-0 tabular-nums">{display}</span>
        ) : (
          <span className="text-sm text-theme-text opacity-40 shrink-0">--</span>
        )}
        {showChart ? (
          <div className="shrink-0 flex items-end pt-0.5" aria-hidden>
            <MiniDistribution distribution={distribution} patternMedian={value} compact />
          </div>
        ) : null}
      </div>
    </td>
  );
};

const VideoRow = ({ item, onRowClick, pageDistributions, showFitColumn }) => {
  const video = item.video;
  const creator = item.creator;
  const platform = getPlatform(video?.platform);
  const title = video?.title || video?.platform_id || "—";
  const creatorName = creator?.name || creator?.platform_id || "—";
  const creatorSubs = creator?.follower_count != null ? ` (${formatNumber(creator.follower_count)})` : "";
  const engagementPct = formatEngagementPct(item.engagement);
  const viewCount = video?.count_views;
  const likeCount = video?.count_likes;
  const commentCount = video?.count_comments;
  const engagementNum = item.engagement != null ? Number(item.engagement) : null;
  const dist = pageDistributions || {};
  const showViewsChart = Boolean(dist.views?.length && viewCount != null && viewCount > 0);
  const showLikesChart = Boolean(dist.likes?.length && likeCount != null && likeCount > 0);
  const showCommentsChart = Boolean(dist.comments?.length && commentCount != null && commentCount > 0);
  const showEngagementChart = Boolean(
    dist.engagement?.length
      && engagementNum != null
      && !Number.isNaN(engagementNum)
      && engagementNum !== 0
      && (video?.count_views ?? 0) > 0
  );

  return (
    <tr
      className="hover:bg-theme-secondary transition-colors cursor-pointer"
      onClick={() => onRowClick(item)}
    >
      <td className="px-4 py-3 max-w-[400px] w-[400px]">
        <div className="flex items-center gap-3 min-w-0">
          {platform?.icon ? (
            <div
              className="flex-shrink-0 w-8 h-8 rounded flex items-center justify-center text-white"
              style={{ backgroundColor: platform.color ?? "var(--theme-text, #6b7280)" }}
              title={platform.name}
            >
              {React.cloneElement(platform.icon, { className: "w-4 h-4" })}
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-theme-text truncate" title={title}>
              {title}
            </div>
            <div className="text-xs text-theme-text opacity-60 truncate" title={creatorName + creatorSubs}>
              {creatorName}{creatorSubs}
            </div>
          </div>
        </div>
      </td>

      <VideoMetricWithChart
        value={viewCount}
        distribution={dist.views}
        display={viewCount != null && viewCount > 0 ? formatNumber(viewCount) : null}
        showChart={showViewsChart}
      />
      <td className="px-4 py-3 whitespace-nowrap">
        <VsCreatorCell outlierRatio={item.creator_views_outlier} />
      </td>

      <VideoMetricWithChart
        value={likeCount}
        distribution={dist.likes}
        display={likeCount != null && likeCount > 0 ? formatNumber(likeCount) : null}
        showChart={showLikesChart}
      />
      <td className="px-4 py-3 whitespace-nowrap">
        <VsCreatorCell outlierRatio={item.creator_likes_outlier} />
      </td>

      <VideoMetricWithChart
        value={commentCount}
        distribution={dist.comments}
        display={commentCount != null && commentCount > 0 ? formatNumber(commentCount) : null}
        showChart={showCommentsChart}
      />
      <td className="px-4 py-3 whitespace-nowrap">
        <VsCreatorCell outlierRatio={item.creator_comments_outlier} />
      </td>

      <VideoMetricWithChart
        value={engagementNum}
        distribution={dist.engagement}
        display={engagementPct}
        showChart={showEngagementChart}
      />
      <td className="px-4 py-3 whitespace-nowrap">
        <VsCreatorCell outlierRatio={item.creator_engagement_outlier} />
      </td>

      {showFitColumn ? (
        <td className="px-4 py-3 whitespace-nowrap text-center" title={TOOLTIP.fit}>
          {item.fit_score != null ? (
            <FitScoreRadial score={item.fit_score} />
          ) : (
            <span className="text-sm text-theme-text opacity-40">--</span>
          )}
        </td>
      ) : null}

      <td className="px-4 py-3 whitespace-nowrap text-sm text-theme-text">
        {formatUploadedAt(video?.uploaded_at)}
      </td>
    </tr>
  );
};

const Videos = ({ audience }) => {
  const navigate = useNavigate();
  const { videoHash } = useParams();
  const [videos, setVideos] = useState([]);
  const [pageMeta, setPageMeta] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [prevPageUrl, setPrevPageUrl] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("best");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [dateUploadedFilter, setDateUploadedFilter] = useState("month");
  const [creatorSizeFilter, setCreatorSizeFilter] = useState("all");

  const baseUrl = `/audience/${audience?.hash}/videos/`;

  const showFitColumn = useMemo(
    () => videos.some((v) => v.fit_score != null),
    [videos]
  );

  const handleRowClick = (item) => {
    navigate(`/audience/${audience?.hash}/videos/${item.hash}/`);
  };

  const fetchVideos = useCallback(() => {
    if (!audience?.hash) {
      setVideos([]);
      setPageMeta(null);
      setTotalCount(0);
      setNextPageUrl(null);
      setPrevPageUrl(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const params = {
      audience_hash: audience.hash,
      sort_by: sortBy,
      sort_direction: sortDirection,
      page,
      page_size: PAGE_SIZE,
    };
    if (search) params.search = search;
    if (dateUploadedFilter && dateUploadedFilter !== "all") params.date_uploaded = dateUploadedFilter;
    if (creatorSizeFilter && creatorSizeFilter !== "all") params.creator_size = creatorSizeFilter;

    audiencesService.getAudienceVideos(
      params,
      (response) => {
        const { results, count, next, previous, meta } = parsePaginatedResponse(response);
        setVideos(results);
        setPageMeta(meta);
        setTotalCount(count);
        setNextPageUrl(next);
        setPrevPageUrl(previous);
        setLoading(false);
      },
      () => {
        setVideos([]);
        setPageMeta(null);
        setTotalCount(0);
        setNextPageUrl(null);
        setPrevPageUrl(null);
        setLoading(false);
      }
    );
  }, [audience?.hash, page, search, sortBy, sortDirection, dateUploadedFilter, creatorSizeFilter]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [audience?.hash, sortBy, sortDirection, dateUploadedFilter, creatorSizeFilter]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const handleSort = (sortKey) => {
    if (sortKey === sortBy) {
      setSortDirection((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(sortKey);
      setSortDirection("desc");
    }
    setPage(1);
  };

  const totalPages = getTotalPages(totalCount, PAGE_SIZE);
  const showPagination = videos.length > 0 && totalPages > 1;
  const pageDistributions = useMemo(
    () => ({
      views: pageMeta?.videos?.views?.distribution ?? null,
      likes: pageMeta?.videos?.likes?.distribution ?? null,
      comments: pageMeta?.videos?.comments?.distribution ?? null,
      engagement: pageMeta?.videos?.engagement_rate?.distribution ?? null,
    }),
    [pageMeta]
  );
  const videoSortThProps = {
    sortBy,
    sortDirection,
    onSort: handleSort,
    showSortArrow: false,
    activeClassName: "patterns-sort-th-active",
  };

  if (loading && videos.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <OrbitalLoader size={128} />
      </div>
    );
  }

  if (videos.length === 0) {
    const hasActiveFilters = search || dateUploadedFilter !== "all" || creatorSizeFilter !== "all";
    const handleResetFilters = () => {
      setSearchInput("");
      setSearch("");
      setDateUploadedFilter("all");
      setCreatorSizeFilter("all");
      setPage(1);
    };
    return (
      <div className="text-center py-12">
        <div className="text-theme-text opacity-80 mb-4">
          <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-theme-text mb-2">
          No videos that meet these filters
        </h3>
        <p className="text-theme-text opacity-60 mb-4">
          {hasActiveFilters
            ? "Try changing the filters or reset them to see all videos."
            : "High-fit videos for this audience will appear here once analysis is complete."}
        </p>
        {hasActiveFilters && (
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90"
          >
            Reset filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-theme-text">Videos ({totalCount})</h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search videos"
            className="px-3 py-2 bg-theme-secondary border border-theme-secondary rounded-md text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
          />
          <select
            value={dateUploadedFilter}
            onChange={(e) => { setDateUploadedFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 bg-theme-secondary border border-theme-secondary rounded-md text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
            aria-label="Date uploaded"
          >
            <option value="all">Date uploaded: All</option>
            <option value="year">Year</option>
            <option value="month">Month</option>
            <option value="week">Week</option>
            <option value="day">Day</option>
          </select>
          <select
            value={creatorSizeFilter}
            onChange={(e) => { setCreatorSizeFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 bg-theme-secondary border border-theme-secondary rounded-md text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
            aria-label="Creator size"
          >
            <option value="all">Creator size: All</option>
            <option value="mongo">Mongo (10M+)</option>
            <option value="mega">Mega (1M+)</option>
            <option value="macro">Macro (100K-1M)</option>
            <option value="mid">Mid (10K-100K)</option>
            <option value="micro">Micro (1K-10K)</option>
            <option value="nano">Nano (&lt;1K)</option>
          </select>
        </div>
      </div>

      <div
        className={`rounded-lg border border-theme-secondary bg-theme-main overflow-hidden transition-opacity ${loading ? "opacity-60 pointer-events-none" : ""}`}
        aria-busy={loading}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-theme-secondary border-b border-theme-secondary">
                <th className="px-4 py-2 text-left text-xs font-medium text-theme-text uppercase tracking-wider max-w-[400px] w-[400px]">
                  Video
                </th>
                <SortableTh {...videoSortThProps} label="Views" tooltip="Sort by view count. Mini charts compare each row to the other videos on this page only." sortKey="views" align="right" />
                <SortableTh {...videoSortThProps} label="Outlier" tooltip="Difference vs the median of this creator's views. +50% means this video has 50% more views than the creator's median." sortKey="creator_views_outlier" />
                <SortableTh {...videoSortThProps} label="Likes" tooltip="Sort by like count. Mini chart uses like counts on this page only." sortKey="likes" align="right" />
                <SortableTh {...videoSortThProps} label="Outlier" tooltip="Difference vs the median of this creator's likes. +50% means this video has 50% more likes than the creator's median." sortKey="creator_likes_outlier" />
                <SortableTh {...videoSortThProps} label="Comments" tooltip="Sort by comment count. Mini chart uses comment counts on this page only." sortKey="comments" align="right" />
                <SortableTh {...videoSortThProps} label="Outlier" tooltip="Difference vs the median of this creator's comments. +50% means this video has 50% more comments than the creator's median." sortKey="creator_comments_outlier" />
                <SortableTh {...videoSortThProps} label="Engagement" tooltip="Sort by engagement (likes+comments)/views. Mini chart uses engagement rates on this page only." sortKey="engagement" align="right" />
                <SortableTh {...videoSortThProps} label="Outlier" tooltip="Difference vs the median of this creator's engagement. +50% means this video has 50% higher engagement than the creator's median." sortKey="creator_engagement_outlier" />
                {showFitColumn ? (
                  <SortableTh {...videoSortThProps} label="Fit" tooltip={TOOLTIP.fit} sortKey="fit_score" />
                ) : null}
                <SortableTh {...videoSortThProps} label="Uploaded" tooltip="Sort by video upload date" sortKey="uploaded_at" />
              </tr>
            </thead>
            <tbody className="bg-theme-main divide-y divide-theme-secondary">
              {videos.map((item) => (
                <VideoRow
                  key={item.hash}
                  item={item}
                  onRowClick={handleRowClick}
                  pageDistributions={pageDistributions}
                  showFitColumn={showFitColumn}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showPagination ? (
        <div className="flex items-center justify-between pt-1">
          <p className="text-xs text-theme-text opacity-70">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setPage((v) => Math.max(1, v - 1));
                smoothScrollAudiencePageTop();
              }}
              disabled={!prevPageUrl || loading}
              className="px-3 py-1.5 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-main disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => {
                setPage((v) => v + 1);
                smoothScrollAudiencePageTop();
              }}
              disabled={!nextPageUrl || loading}
              className="px-3 py-1.5 rounded border border-theme-secondary text-sm text-theme-text hover:bg-theme-main disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}

      {videoHash && (
        <VideoDetailModal
          audienceHash={audience?.hash}
          videoHash={videoHash}
          baseUrl={baseUrl}
        />
      )}
    </div>
  );
};

export default Videos;
